import React, { useLayoutEffect } from 'react';
import {
  PageContainer,
  ContentContainer,
  Content,
  Text,
  Title,
  InfoSectionsContainer,
  InfoSection,
  SectionTitle,
  SectionText,
  BulletList,
  ClosingSection,
  StepsHorizontalContainer,
  StepItem,
  StepNumber,
  StepText,
  ArrowIcon,
  StepWithArrow,
  UpcommingTreksContent,
  Subtitle,
  UpcomingTextBlock,
  UpcomingHighlight,
  UpcomingCtaBox,
} from './Trekking.styled';
import { useTranslation, Trans } from 'react-i18next';

export const Trekking = () => {
  const { t } = useTranslation();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageContainer>
      <ContentContainer>
        <Content>
          <Title>{t('trekking.hero.title')}</Title>
          <Text>{t('trekking.hero.intro')}</Text>
        </Content>
        <Content>
          <SectionTitle style={{ textAlign: 'center', marginBottom: '20px' }}>
            {t('trekking.howToSignUp.title')}
          </SectionTitle>
          <StepsHorizontalContainer>
            {(t('trekking.howToSignUp.steps', { returnObjects: true }) as string[]).map(
              (step: string, index: number) => (
                <StepWithArrow key={index}>
                  <StepItem>
                    <StepNumber>{index + 1}</StepNumber>
                    <StepText>{step}</StepText>
                  </StepItem>
                  {index <
                    (t('trekking.howToSignUp.steps', { returnObjects: true }) as string[]).length -
                      1 && <ArrowIcon>→</ArrowIcon>}
                </StepWithArrow>
              ),
            )}
          </StepsHorizontalContainer>
        </Content>
      </ContentContainer>

      <InfoSectionsContainer>
        <InfoSection>
          <SectionTitle>{t('trekking.whatWeDo.title')}</SectionTitle>
          <SectionText>{t('trekking.whatWeDo.text1')}</SectionText>
          <SectionText>{t('trekking.whatWeDo.text2')}</SectionText>
        </InfoSection>

        <InfoSection>
          <SectionTitle>{t('trekking.forWho.title')}</SectionTitle>
          <SectionText>{t('trekking.forWho.intro')}</SectionText>
          <SectionText style={{ marginTop: '15px' }}>{t('trekking.forWho.subtitle')}</SectionText>
          <BulletList>
            {(t('trekking.forWho.points', { returnObjects: true }) as string[]).map(
              (point: string, index: number) => (
                <li key={index}>{point}</li>
              ),
            )}
          </BulletList>
        </InfoSection>

        <InfoSection>
          <SectionTitle>{t('trekking.organization.title')}</SectionTitle>
          <SectionText>{t('trekking.organization.intro')}</SectionText>
          <BulletList>
            {(t('trekking.organization.points', { returnObjects: true }) as string[]).map(
              (point: string, index: number) => (
                <li key={index}>{point}</li>
              ),
            )}
          </BulletList>
        </InfoSection>

        <InfoSection>
          <SectionTitle>{t('trekking.whatToBring.title')}</SectionTitle>
          <BulletList>
            {(t('trekking.whatToBring.points', { returnObjects: true }) as string[]).map(
              (point: string, index: number) => (
                <li key={index}>{point}</li>
              ),
            )}
          </BulletList>
        </InfoSection>

        <InfoSection>
          <SectionTitle>{t('trekking.afterTrek.title')}</SectionTitle>
          <SectionText>{t('trekking.afterTrek.text1')}</SectionText>
          <SectionText>{t('trekking.afterTrek.text2')}</SectionText>
          <SectionText>{t('trekking.afterTrek.text3')}</SectionText>
        </InfoSection>

        <InfoSection>
          <SectionTitle>{t('trekking.importantInfo.title')}</SectionTitle>
          <SectionText>{t('trekking.importantInfo.text1')}</SectionText>
          <SectionText>{t('trekking.importantInfo.text2')}</SectionText>
          <SectionText style={{ marginTop: '15px' }}>
            {t('trekking.importantInfo.text3')}
          </SectionText>
          <SectionText>{t('trekking.importantInfo.text4')}</SectionText>
          <SectionText>{t('trekking.importantInfo.text5')}</SectionText>
        </InfoSection>

        <InfoSection>
          <SectionTitle>{t('trekking.cancellationPolicy.title')}</SectionTitle>
          <SectionText style={{ fontWeight: '400' }}>
            {t('trekking.cancellationPolicy.text1')}
          </SectionText>
          <SectionText style={{ marginTop: '15px' }}>
            {t('trekking.cancellationPolicy.text2')}
          </SectionText>
          <BulletList>
            {(
              t('trekking.cancellationPolicy.options', {
                returnObjects: true,
              }) as string[]
            ).map((option: string, index: number) => (
              <li key={index}>{option}</li>
            ))}
          </BulletList>
          <SectionText style={{ marginTop: '15px' }}>
            {t('trekking.cancellationPolicy.text3')}
          </SectionText>
        </InfoSection>

        <InfoSection>
          <SectionTitle>{t('trekking.responsibility.title')}</SectionTitle>
          <SectionText>{t('trekking.responsibility.text1')}</SectionText>
          <SectionText style={{ fontWeight: '400' }}>
            {t('trekking.responsibility.text2')}
          </SectionText>
        </InfoSection>

        <InfoSection>
          <SectionTitle>{t('trekking.contact.title')}</SectionTitle>
          <SectionText>{t('trekking.contact.text1')}</SectionText>
          <SectionText style={{ fontWeight: '400', marginTop: '10px' }}>
            {t('trekking.contact.email')}
          </SectionText>
        </InfoSection>

        <ClosingSection>{t('trekking.closing')}</ClosingSection>
      </InfoSectionsContainer>

      <UpcommingTreksContent>
        <Subtitle>{t('trekking.upcomingTreks.title')}</Subtitle>
        <UpcomingTextBlock $spacing="large">
          <Trans
            i18nKey="trekking.upcomingTreks.text1"
            components={{ highlight: <UpcomingHighlight /> }}
          />
        </UpcomingTextBlock>
        <UpcomingTextBlock>
          <Trans
            i18nKey="trekking.upcomingTreks.text2"
            components={{ highlight: <UpcomingHighlight /> }}
          />
        </UpcomingTextBlock>
        <UpcomingTextBlock $spacing="large">{t('trekking.upcomingTreks.text3')}</UpcomingTextBlock>
        <UpcomingTextBlock>
          <Trans
            i18nKey="trekking.upcomingTreks.text4"
            components={{ highlight: <UpcomingHighlight /> }}
          />
        </UpcomingTextBlock>
        <UpcomingCtaBox>
          <a href="mailto:hikematters.info@gmail.com">{t('trekking.upcomingTreks.cta')}</a>
        </UpcomingCtaBox>
      </UpcommingTreksContent>
    </PageContainer>
  );
};
